import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// reactstrap components
import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
  Button,
  Input,
} from "reactstrap";

// auth
import { useAuth } from 'contexts/AuthContext';

// services
import apiService from '../../../service/apiService';
// COMENTADO - Solo usar token de Keycloak, no useToken de TokenContext
// import { useToken } from 'contexts/TokenContext';

// components
import MerchantTable from './components/MerchantTable';
import SearchFilters from './components/SearchFilters';
import { formatNumber } from '../../../utils/formatUtils';

function MerchantSearch() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // COMENTADO - Solo usar token de Keycloak, no useToken
  // const { hasToken, isConfigured } = useToken();
  const hasToken = () => apiService.hasToken();
  const isConfigured = true;
  const [filters, setFilters] = useState({});
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [simpleSearch, setSimpleSearch] = useState('');
  const [selectedType, setSelectedType] = useState(null); // null = all, 'DEALER', 'MONITORING_CENTER'

  // No mock data - forces API usage

  const handleFilterChange = (newFilters) => {
    console.log('🔄 handleFilterChange called with:', newFilters);
    setFilters(newFilters);
    console.log('✅ Filters state updated');
  };

  // Handle type filter with toggle functionality
  const handleTypeFilter = async (type, page = 0) => {
    console.log('🔄 handleTypeFilter called with:', type, 'page:', page);
    if (isLoading) return;
    
    // Toggle functionality: if clicking the same type, deselect it
    const nextType = selectedType === type ? null : type;
    setSelectedType(nextType);
    setCurrentPage(page);
    
    if (!hasToken() || !isConfigured) {
      setError('Token required to filter merchants by type');
      setFilteredMerchants([]);
      setTotalElements(0);
      setTotalPages(0);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      if (nextType === null) {
        // Load all merchants
        await loadMerchants(page, pageSize);
      } else {
        // Load merchants by type
        const data = await apiService.getMerchantsByType(nextType, pageSize, page);
        
        if (data && data.content && Array.isArray(data.content)) {
          setFilteredMerchants(data.content);
          setTotalElements(data.totalElements || 0);
          setTotalPages(data.totalPages || 0);
          setCurrentPage(page);
        } else {
          setFilteredMerchants([]);
          setTotalElements(0);
          setTotalPages(0);
        }
      }
    } catch (error) {
      console.error('Type filter failed:', error);
      setError(`Failed to filter merchants by type: ${error.message}`);
      setFilteredMerchants([]);
      setTotalElements(0);
      setTotalPages(0);
    } finally {
      setIsLoading(false);
    }
  };



  const [filteredMerchants, setFilteredMerchants] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Pagination state for API
  const [currentPage, setCurrentPage] = useState(0); // API uses 0-based pages
  const [pageSize, setPageSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Track initial load to prevent double requests
  const initialLoadDone = useRef(false);
  const searchDebounceTimer = useRef(null);



  // Load merchants with pagination support
  const loadMerchants = async (page = currentPage, size = pageSize) => {
    if (!hasToken() || !isConfigured) {
      setFilteredMerchants([]);
      setTotalElements(0);
      setTotalPages(0);
      setError('Token required to load merchants from API');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await apiService.getMerchants(size, page);
      
      if (data && data.content && Array.isArray(data.content)) {
        setFilteredMerchants(data.content);
        setTotalElements(data.totalElements || 0);
        setTotalPages(data.totalPages || 0);
        setCurrentPage(data.page !== undefined ? data.page : page);
        setPageSize(data.size || size);
      } else if (data && Array.isArray(data)) {
        setFilteredMerchants(data);
        setTotalElements(data.length);
        setTotalPages(1);
        setCurrentPage(0);
      } else {
        setError('Invalid API response structure');
        setFilteredMerchants([]);
        setTotalElements(0);
        setTotalPages(0);
      }
    } catch (error) {
      console.error('Failed to load merchants:', error);
      setError(`Failed to load merchants: ${error.message}`);
      setFilteredMerchants([]);
      setTotalElements(0);
      setTotalPages(0);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper: format digits to Federal Tax ID (XX-XXXXXXX)
  const formatDigitsToFederalTaxId = (raw) => {
    const digits = String(raw || '').replace(/\D/g, '').slice(0, 9);
    if (digits.length <= 2) return digits;
    return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  };

  // Simple search: pass user input directly to the search endpoint
  const searchByLegalName = async (term, page = 0, size = 10) => {
    console.log('🔎 searchByLegalName called with:', term);
    setError(null);
    setIsLoading(true);

    if (!hasToken() || !isConfigured) {
      setError('Token required to search merchants via API');
      setFilteredMerchants([]);
      setTotalElements(0);
      setTotalPages(0);
      setIsLoading(false);
      return;
    }

    try {
      const query = (term || '').trim();
      
      // Use the simple search endpoint that takes the user input directly as 'q' parameter
      const data = await apiService.searchMerchants(query, page, size);

      if (data && data.content && Array.isArray(data.content)) {
        setFilteredMerchants(data.content);
        setTotalElements(data.totalElements || 0);
        setTotalPages(data.totalPages || 0);
        setCurrentPage(data.page !== undefined ? data.page : page);
        setPageSize(data.size || size);
      } else {
        setFilteredMerchants([]);
        setTotalElements(0);
        setTotalPages(0);
        setCurrentPage(0);
      }
    } catch (error) {
      console.error('Simple search failed:', error);
      setError(`Search failed: ${error.message}`);
      setFilteredMerchants([]);
      setTotalElements(0);
      setTotalPages(0);
    } finally {
      setIsLoading(false);
    }
  };

  // Load merchants on component mount only (once)
  useEffect(() => {
    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      loadMerchants();
    }
    
    // Cleanup debounce timer on unmount
    return () => {
      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current);
      }
    };
  }, []);

  const handleSearch = async (searchFilters = filters, page = 0, size = pageSize) => {
    console.log('🚀 handleSearch called with filters:', searchFilters);
    setError(null);
    setIsLoading(true);

    if (!hasToken() || !isConfigured) {
      setError('Token required to search merchants via API');
      setFilteredMerchants([]);
      setTotalElements(0);
      setTotalPages(0);
      setIsLoading(false);
      return;
    }

    try {
      const merchantFilterRequestDto = apiService.transformFiltersToApi(searchFilters);
      console.log('🔍 Searching with filters:', { original: searchFilters, merchantFilterRequestDto });
      
      const data = await apiService.filterMerchants(merchantFilterRequestDto, page, size);
      
      if (data && data.content && Array.isArray(data.content)) {
        setFilteredMerchants(data.content);
        setTotalElements(data.totalElements || 0);
        setTotalPages(data.totalPages || 0);
        setCurrentPage(data.page !== undefined ? data.page : page);
        setPageSize(data.size || size);
      } else {
        await loadMerchants(page, size);
      }
    } catch (error) {
      console.error('Search failed:', error);
      setError(`Search failed: ${error.message}`);
      await loadMerchants(page, size);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle page changes
  const handlePageChange = (newPage) => {
    if (simpleSearch && simpleSearch.trim() !== '') {
      // When simple search is active, keep using the single-filter search
      searchByLegalName(simpleSearch, newPage, 10);
    } else if (selectedType !== null) {
      // If type filter is active, use type filter
      handleTypeFilter(selectedType, newPage);
    } else if (Object.keys(filters).length > 0) {
      // If there are active filters, use search
      handleSearch(filters, newPage, pageSize);
    } else {
      // Otherwise load merchants normally
      loadMerchants(newPage, pageSize);
    }
  };

  // Handle page size changes  
  const handlePageSizeChange = (newSize) => {
    const validatedSize = Math.min(Math.max(1, newSize), 100); // Enforce max 100
    setCurrentPage(0); // Reset to first page
    
    if (simpleSearch && simpleSearch.trim() !== '') {
      // Keep simple search active on page size change; API size fixed to 10 as requested
      searchByLegalName(simpleSearch, 0, 10);
    } else if (Object.keys(filters).length > 0) {
      // If there are active filters, use search
      handleSearch(filters, 0, validatedSize);
    } else {
      // Otherwise load merchants normally
      loadMerchants(0, validatedSize);
    }
  };

  const handleClear = () => {
    setFilters({});
    setError(null);
    setCurrentPage(0); // Reset to first page
    // Reload all merchants from first page
    loadMerchants(0, pageSize);
  };

  const handleAddMerchant = () => {
    navigate('/wizard/merchant/new');
  };

  const toggleAdvancedSearch = () => {
    setShowAdvancedSearch(!showAdvancedSearch);
  };

  const handleSimpleSearch = (searchTerm) => {
    const query = searchTerm || '';
    setSimpleSearch(query);
    
    // Clear any existing timer
    if (searchDebounceTimer.current) {
      clearTimeout(searchDebounceTimer.current);
    }
    
    // Only debounce if initial load is done
    if (initialLoadDone.current) {
      searchDebounceTimer.current = setTimeout(() => {
        const trimmedQuery = query.trim();
        
        if (!trimmedQuery) {
          // If empty, show all merchants
          setFilters({});
          setSelectedType(null);
          setCurrentPage(0);
          loadMerchants(0, pageSize);
        } else {
          // Clear advanced filters when doing simple search
          setFilters({});
          setSelectedType(null);
          // Pass the user input directly to the search endpoint
          searchByLegalName(trimmedQuery, 0, 10);
        }
      }, 500);
    }
  };

  return (
    <Container className="mt-4" fluid>
             {/* Header Section */}
       <Row className="mb-4">
         <Col>
           <Card className="bg-gradient-info text-white mb-0">
             <CardBody className="py-3">
               <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
                 <div className="mb-2 mb-md-0">
                   <h3 className="text-white mb-0">
                     <i className="ni ni-briefcase-24 mr-2"></i>
                     Merchant List
                   </h3>
                   <p className="text-white mb-0 mt-1" style={{ opacity: 0.9 }}>
                     Manage and search merchant information
                     {totalElements > 0 && (
                       <span className="ml-2 badge badge-light">
                         {formatNumber(totalElements)} merchant{totalElements !== 1 ? 's' : ''}
                       </span>
                     )}
                   </p>
                 </div>
                  <div className="d-flex flex-column flex-sm-row gap-2 header-buttons-mobile">
                   <Button 
                     color="light" 
                     size="sm" 
                     onClick={toggleAdvancedSearch}
                     style={{ backgroundColor: '#e3f2fd', borderColor: '#bbdefb', color: '#1976d2' }}
                     className="mb-2 mb-sm-0"
                   >
                     <i className={`ni ${showAdvancedSearch ? 'ni-fat-remove' : 'ni-fat-add'} mr-2`}></i>
                     <span className="d-none d-sm-inline">
                       {showAdvancedSearch ? 'Hide Advanced Search' : 'Advanced Search'}
                     </span>
                     <span className="d-inline d-sm-none">
                       {showAdvancedSearch ? 'Hide Search' : 'Advanced'}
                     </span>
                   </Button>
                   <Button color="success" size="sm" onClick={handleAddMerchant}>
                     <i className="ni ni-fat-add mr-2"></i>
                     <span className="d-none d-sm-inline">Add Merchant</span>
                     <span className="d-inline d-sm-none">Add</span>
                   </Button>
                 </div>
               </div>
             </CardBody>
           </Card>
         </Col>
       </Row>

       {/* Simple Search Section */}
       <Row className="mb-3">
         <Col lg="6" md="12" className="mb-3 mb-lg-0">
           <div style={{ maxWidth: '400px', width: '100%' }}>
             <div className="position-relative">
                                <Input
                   type="text"
                   placeholder="Buscar..."
                   value={simpleSearch}
                   onChange={(e) => handleSimpleSearch(e.target.value)}
                   style={{
                     backgroundColor: '#ffffff',
                     border: '1px solid #e9ecef',
                     borderRadius: '25px',
                     padding: '12px 20px',
                     fontSize: '0.9rem',
                     boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                     width: '100%'
                   }}
                 />
               <div className="position-absolute" style={{ right: '15px', top: '50%', transform: 'translateY(-50%)' }}>
                 <i className="ni ni-zoom-split-in text-muted" style={{ fontSize: '0.9rem' }}></i>
               </div>
               {simpleSearch && (
                 <Button
                   color="link"
                   size="sm"
                   onClick={() => handleSimpleSearch('')}
                   className="position-absolute p-0"
                   style={{ 
                     right: '40px', 
                     top: '50%', 
                     transform: 'translateY(-50%)',
                     color: '#6c757d',
                     fontSize: '0.8rem'
                   }}
                   title="Clear search"
                 >
                   <i className="ni ni-fat-remove"></i>
                 </Button>
               )}
             </div>
           </div>
         </Col>
         <Col lg="6" md="12">
             <div className="status-filters-container">
               <div className="d-flex align-items-center flex-nowrap">
                 <span className="text-muted mr-2 status-label-text" style={{ fontSize: '0.78rem' }}>
                   Filter by type:
                 </span>
                 <div className="status-filters-wrapper">
                   <div className="status-filters-scroll">
                     <button
                       onClick={() => handleTypeFilter('DEALER')}
                       className={`status-filter-pill compact ${selectedType === 'DEALER' ? 'active' : ''}`}
                       style={{
                         fontSize: '0.7rem',
                         padding: '4px 8px'
                       }}
                     >
                       <span className="status-label">Dealers</span>
                     </button>
                     <button
                       onClick={() => handleTypeFilter('MONITORING_CENTER')}
                       className={`status-filter-pill compact ${selectedType === 'MONITORING_CENTER' ? 'active' : ''}`}
                       style={{
                         fontSize: '0.7rem',
                         padding: '4px 8px'
                       }}
                     >
                       <span className="status-label">Monitoring Centers</span>
                     </button>
                     {selectedType && (
                       <button
                         onClick={() => {
                           setSelectedType(null);
                           setCurrentPage(0);
                           loadMerchants(0, pageSize);
                         }}
                         className="status-filter-pill clear-btn compact"
                         style={{
                           fontSize: '0.7rem',
                           padding: '4px 8px'
                         }}
                       >
                         <i className="ni ni-fat-remove"></i>
                         <span>Clear</span>
                       </button>
                     )}
                   </div>
                 </div>
               </div>
             </div>
         </Col>
       </Row>

      {/* Search Criteria Section */}
      {showAdvancedSearch && (
        <Row className="mb-4">
          <Col>
            <Card className="shadow">
              <CardHeader className="border-0">
                <h3 className="mb-0">Search Criteria</h3>
              </CardHeader>
              <CardBody>
                <SearchFilters 
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  onSearch={handleSearch}
                  onClear={handleClear}
                  isLoading={isLoading}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

             {/* Merchant List Section */}
       <Row>
         <Col>
           <Card className="shadow">
            <CardBody>
              <MerchantTable 
                merchants={filteredMerchants}
                isLoading={isLoading}
                error={error}
                // API pagination props
                currentPage={currentPage}
                pageSize={pageSize}
                totalElements={totalElements}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                useApiPagination={true}
                onRefresh={() => {
                  if (Object.keys(filters).length > 0) {
                    handleSearch(filters, currentPage, pageSize);
                  } else {
                    loadMerchants(currentPage, pageSize);
                  }
                }}
              />
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default MerchantSearch;
